import { useState, useEffect } from "react";
import { courseBuilderStateUtils } from "../../../utils/course-builder/courseBuilderUtils";
import { useParams, Navigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { ArrowLeft } from "lucide-react";
import CourseBuilderForm from "../../../components/course-builder/CourseBuilderForm";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { permissionStore, PermissionAction, ResourceType } from "../../../stores/permissionStore";
import { toast } from "sonner";

export default function CourseBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const permSnap = useSnapshot(permissionStore);
  const [mode, setMode] = useState("ADD");
  const [currentStep, setCurrentStep] = useState(0);
  
  // Permission checks
  const canCreateCourse = permSnap.can(PermissionAction.CREATE, ResourceType.COURSE);
  const canUpdateCourse = permSnap.can(PermissionAction.UPDATE, ResourceType.COURSE);
  const canReadCourse = permSnap.can(PermissionAction.READ, ResourceType.COURSE);

  // Load saved state on component mount
  useEffect(() => {
    const savedState = courseBuilderStateUtils.getState();
    if (courseId) {
      setMode("VIEW");
      setCurrentStep(savedState?.currentStep || 0);
    } else {
      setMode(savedState?.mode || "ADD");
      setCurrentStep(savedState?.currentStep || 0);
    }
  }, []);

  // Save state changes to localStorage
  useEffect(() => {
    courseBuilderStateUtils.saveState(mode, currentStep);
  }, [mode, currentStep]);

  const handleEditMode = () => {
    if (!canUpdateCourse) {
      toast.error("You don't have permission to edit courses");
      return;
    }
    setMode("EDIT");
    courseBuilderStateUtils.saveState("EDIT", currentStep);
  };

  // Check permissions on mount
  useEffect(() => {
    if (courseId) {
      // Viewing/editing existing course
      if (mode === "VIEW" && !canReadCourse) {
        toast.error("You don't have permission to view courses");
        navigate("/admin/manage-courses");
      } else if (mode === "EDIT" && !canUpdateCourse) {
        toast.error("You don't have permission to edit courses");
        setMode("VIEW");
      }
    } else {
      // Creating new course
      if (!canCreateCourse) {
        toast.error("You don't have permission to create courses");
        navigate("/admin/manage-courses");
      }
    }
  }, [courseId, mode, canCreateCourse, canUpdateCourse, canReadCourse, navigate]);

  return (
    <div className="w-full relative space-y-6">
      <div className="relative w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4 w-full relative">
          {mode === "ADD" ? (
            <></>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                console.log("Clicked");
                if (mode === "ADD") return;
                if (mode === "VIEW") {
                  navigate(-1);
                  return;
                }
                setMode((prev) => {
                  if (prev === "EDIT") return "VIEW";
                  return prev;
                });
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center justify-between w-full relative">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {mode === "ADD"
                  ? "Create New Course"
                  : mode === "VIEW"
                    ? "View Course"
                    : "Edit Course"}
              </h1>
              <p className="text-muted-foreground">
                {mode === "ADD"
                  ? "Fill in the details to create a new course"
                  : mode === "VIEW"
                    ? "Course details (view only)"
                    : "Update your course details"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {mode === "VIEW" && canUpdateCourse && (
                <Button
                  type="button"
                  variant="default"
                  className="mr-2"
                  onClick={handleEditMode}
                >
                  Edit Course
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <CourseBuilderForm
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        mode={mode}
        setMode={setMode}
        courseId={courseId}
      />
    </div>
  );
}
